/**
 * PlatformSiteRootPage — super-admin-only, platform-wide deployment control for
 * the site root page (landing vs sign-in) and the deployment-default login
 * backdrop (image + tagline). Moved out of the tenant Branding page so individual
 * tenants cannot change deployment-wide white-label behaviour. Writes PUT /api/app-config.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Loader2, Monitor, LogIn, Globe, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function PlatformSiteRootPage() {
  const navigate = useNavigate();
  const { user, appConfig, setAppConfig } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [rootPage, setRootPage] = useState(appConfig?.root_page || 'landing');
  const [savingRootPage, setSavingRootPage] = useState(false);
  const [loginBg, setLoginBg] = useState(appConfig?.login_bg_image_url || '');
  const [loginTagline, setLoginTagline] = useState(appConfig?.login_tagline || '');
  const [savingLogin, setSavingLogin] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);

  useEffect(() => { if (appConfig?.root_page) setRootPage(appConfig.root_page); }, [appConfig?.root_page]);
  useEffect(() => { setLoginBg(appConfig?.login_bg_image_url || ''); }, [appConfig?.login_bg_image_url]);
  useEffect(() => { setLoginTagline(appConfig?.login_tagline || ''); }, [appConfig?.login_tagline]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="site-root-denied">
        <p className="text-sm">This is a platform-wide deployment setting. Super-admin access required.</p>
        <Button variant="outline" className="mt-4 rounded-lg" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
      </div>
    );
  }

  const changeRootPage = async (value) => {
    if (value === rootPage || savingRootPage) return;
    const prev = rootPage;
    setRootPage(value);
    setSavingRootPage(true);
    try {
      await api.updateAppConfig({ root_page: value });
      setAppConfig({ ...appConfig, root_page: value });
      toast.success(value === 'login' ? 'Site root now shows the sign-in page' : 'Site root now shows the landing page');
    } catch (err) {
      setRootPage(prev);
      toast.error(err.message || 'Could not update the main page');
    } finally { setSavingRootPage(false); }
  };

  const saveLoginBackdrop = async () => {
    setSavingLogin(true);
    try {
      const res = await api.updateAppConfig({ login_bg_image_url: loginBg, login_tagline: loginTagline });
      setAppConfig({ ...appConfig, login_bg_image_url: res.login_bg_image_url, login_tagline: res.login_tagline });
      toast.success('Deployment login backdrop saved');
    } catch (err) { toast.error(err.message || 'Could not save'); }
    finally { setSavingLogin(false); }
  };

  const onPickBackground = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); return; }
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.uploadLoginBackground(fd);
      setLoginBg(res.url);
      setAppConfig({ ...appConfig, login_bg_image_url: res.url });
      toast.success('Login background uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingBg(false); }
  };

  const removeBackground = async () => {
    try {
      await api.updateAppConfig({ login_bg_image_url: '' });
      setLoginBg('');
      setAppConfig({ ...appConfig, login_bg_image_url: '' });
      toast.success('Login background removed — using the default');
    } catch (err) { toast.error(err.message || 'Could not remove background'); }
  };

  const DEFAULT_BG = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1400&q=70';

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="platform-site-root-page">
      <button onClick={() => navigate('/platform-console/operations')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3" data-testid="site-root-back">
        <ChevronLeft className="w-4 h-4" /> Platform Console
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-semibold text-foreground">Site Root &amp; Login Page</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Platform-wide deployment settings. These apply to <span className="font-medium">every</span> anonymous visitor at the site root and the plain sign-in page — individual tenants cannot change them.
      </p>

      <div className="glass-card rounded-2xl p-6 space-y-5 border border-border/40">
        {/* Main page (site root) */}
        <div data-testid="platform-root-page">
          <Label className="text-xs text-muted-foreground">Main page (site root)</Label>
          <p className="text-[11px] text-muted-foreground mb-2">
            What visitors see at the site root before signing in. Choose <span className="font-medium">Sign-in page</span> for a white-labelled customer deployment so the INZARA landing page is hidden.
          </p>
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <button
              type="button"
              disabled={savingRootPage}
              onClick={() => changeRootPage('landing')}
              className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${rootPage === 'landing' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
              data-testid="root-page-landing"
            >
              <Monitor className={`w-4 h-4 mt-0.5 ${rootPage === 'landing' ? 'text-primary' : 'text-muted-foreground'}`} />
              <span>
                <span className="block text-xs font-medium text-foreground">Landing page</span>
                <span className="block text-[10px] text-muted-foreground">Show the marketing home page</span>
              </span>
            </button>
            <button
              type="button"
              disabled={savingRootPage}
              onClick={() => changeRootPage('login')}
              className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${rootPage === 'login' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
              data-testid="root-page-login"
            >
              <LogIn className={`w-4 h-4 mt-0.5 ${rootPage === 'login' ? 'text-primary' : 'text-muted-foreground'}`} />
              <span>
                <span className="block text-xs font-medium text-foreground">Sign-in page</span>
                <span className="block text-[10px] text-muted-foreground">Go straight to login (no landing)</span>
              </span>
            </button>
          </div>
        </div>

        {/* Deployment default login backdrop + tagline */}
        <div className="pt-4 border-t border-border/40 space-y-2">
          <div>
            <Label className="text-[11px] text-muted-foreground">Login background image (deployment default)</Label>
            <div className="mt-1 flex items-start gap-3">
              <div className="h-16 w-28 shrink-0 rounded-lg border border-border/40 bg-muted/30 overflow-hidden flex items-center justify-center" data-testid="platform-login-bg-preview">
                {loginBg
                  ? <img src={loginBg} alt="Login background preview" className="h-full w-full object-cover" />
                  : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/40 text-xs cursor-pointer hover:bg-muted/30 ${uploadingBg ? 'opacity-60 pointer-events-none' : 'text-foreground'}`} data-testid="platform-login-bg-upload-label">
                    {uploadingBg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploadingBg ? 'Uploading…' : 'Upload image'}
                    <input type="file" accept="image/*" className="hidden" onChange={onPickBackground} disabled={uploadingBg} data-testid="platform-login-bg-file" />
                  </label>
                  {loginBg ? (
                    <button type="button" onClick={removeBackground} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/40 text-xs text-muted-foreground hover:text-red-500 hover:border-red-500/40" data-testid="platform-login-bg-remove">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  ) : null}
                </div>
                <Input value={loginBg} onChange={(e) => setLoginBg(e.target.value)} placeholder="…or paste an image URL" className="text-foreground text-xs" data-testid="platform-login-bg" />
                <p className="text-[10px] text-muted-foreground">PNG, JPG or WebP, up to 8&nbsp;MB. Uploading applies immediately; a pasted URL applies when you Save.</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Login welcome tagline (deployment default)</Label>
            <Input value={loginTagline} onChange={(e) => setLoginTagline(e.target.value)} placeholder="e.g. Welcome to Acme Analytics" maxLength={200} className="mt-1 text-foreground" data-testid="platform-login-tagline" />
          </div>
          <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={saveLoginBackdrop} disabled={savingLogin} data-testid="platform-login-save">
            {savingLogin ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save login backdrop
          </Button>
        </div>
      </div>

      {/* Live sign-in preview */}
      <div className="mt-5" data-testid="login-preview-wrap">
        <Label className="text-xs text-muted-foreground mb-2 block">Live sign-in preview</Label>
        <div
          className="relative rounded-2xl overflow-hidden border border-border/40 h-64 bg-cover bg-center"
          style={{ backgroundImage: `url(${loginBg || DEFAULT_BG})` }}
          data-testid="login-preview"
        >
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative h-full flex items-center justify-center p-6">
            <div className="w-full max-w-xs rounded-2xl bg-background/95 backdrop-blur border border-border/40 p-5 shadow-xl">
              <div className="text-center mb-4">
                <div className="text-base font-semibold text-foreground">Sign in</div>
                {loginTagline
                  ? <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="login-preview-tagline">{loginTagline}</div>
                  : null}
              </div>
              <div className="space-y-2">
                <div className="h-8 rounded-lg border border-border/40 bg-muted/40" />
                <div className="h-8 rounded-lg border border-border/40 bg-muted/40" />
                <div className="h-8 rounded-lg bg-primary/80" />
              </div>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Shows the chosen background and tagline. {loginBg ? '' : 'No background set — using the default image.'}
        </p>
      </div>
    </div>
  );
}
